import { defineStore } from 'pinia'

const LS_KEY = 'notes-v1'
const VERSION = 1

const encodeFirestoreKey = (value: string | number) => encodeURIComponent(String(value))
const decodeFirestoreKey = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}
const getFirestoreInstance = async () => {
  const { $firestore } = useNuxtApp()
  if (!$firestore) return null
  return typeof $firestore === 'function' ? await $firestore() : $firestore
}

export interface StyleNote {
  style: string | number
  note: string
  updatedAt: number
}

interface PersistShape {
  v: number
  notes: Record<string | number, string>
}

export const useNotesStore = defineStore('notes', {
  state: () => ({
    hydrated: false,
    notes: {} as Record<string | number, string>,
    loadingNotes: new Set<string | number>()
  }),
  getters: {
    getNoteForStyle: (state) => (style: string | number): string => {
      return state.notes[style] || ''
    },
    hasNoteForStyle: (state) => (style: string | number): boolean => {
      return !!state.notes[style]?.trim()
    }
  },
  actions: {
    hydrate() {
      if (this.hydrated) return
      if (typeof window === 'undefined') return
      try {
        const raw = localStorage.getItem(LS_KEY)
        if (!raw) {
          this.hydrated = true
          return
        }
        const parsed: PersistShape = JSON.parse(raw)
        if (parsed.v !== VERSION) {
          localStorage.removeItem(LS_KEY)
          this.hydrated = true
          return
        }
        this.notes = parsed.notes || {}
      } catch {
        // ignore
      } finally {
        this.hydrated = true
      }
    },
    persist() {
      if (typeof window === 'undefined') return
      const out: PersistShape = {
        v: VERSION,
        notes: this.notes
      }
      localStorage.setItem(LS_KEY, JSON.stringify(out))
      this.persistToFirestoreDebounced()
    },
    setNote(style: string | number, note: string) {
      this.notes[style] = note
      this.persist()
    },
    deleteNote(style: string | number) {
      delete this.notes[style]
      this.persist()
    },
    async hydrateFromFirestore(userUidOrEmail: string) {
      try {
        const firestore = await getFirestoreInstance()
        if (!firestore) {
          console.error('No $firestore found in Nuxt app!')
          return
        }
        const { doc, getDoc, collection } = await import('firebase/firestore')
        const notesDoc = doc(collection(firestore, 'notes'), userUidOrEmail)
        const snap = await getDoc(notesDoc)
        if (!snap.exists()) {
          this.notes = {}
          this.persist()
          return
        }
        const data = snap.data()
        const decodedNotes: Record<string | number, string> = {}
        for (const [style, note] of Object.entries(data.notes || {})) {
          decodedNotes[decodeFirestoreKey(style)] = note
        }
        this.notes = decodedNotes
        this.persist()
      } catch (err) {
        console.error('hydrateFromFirestore error:', err)
        if (process.client && typeof useToast === 'function') {
          useToast().add({
            title: 'Notes sync failed',
            description: 'Could not load notes from server. Using local notes.',
            color: 'error',
            icon: 'i-lucide-alert-triangle'
          })
        }
      }
    },
    async hydrateStyleNoteFromFirestore(userUidOrEmail: string, style: string | number) {
      // Check if already loading this style
      if (this.loadingNotes.has(style)) {
        return
      }
      
      this.loadingNotes.add(style)
      
      try {
        const firestore = await getFirestoreInstance()
        if (!firestore) {
          console.error('No $firestore found in Nuxt app!')
          return
        }
        const { doc, getDoc, collection } = await import('firebase/firestore')
        const notesDoc = doc(collection(firestore, 'notes'), userUidOrEmail)
        const snap = await getDoc(notesDoc)
        if (snap.exists()) {
          const data = snap.data()
          const styleNote = data.notes?.[encodeFirestoreKey(style)]
          if (styleNote) {
            this.notes[style] = styleNote
          }
        }
      } catch (err) {
        console.error('hydrateStyleNoteFromFirestore error:', err)
      } finally {
        this.loadingNotes.delete(style)
      }
    },
    async persistToFirestore(userUidOrEmail?: string) {
      try {
        const firestore = await getFirestoreInstance()
        if (!firestore) {
          console.error('No $firestore found in Nuxt app!')
          return
        }
        let userEmail = ''
        if (!userUidOrEmail) {
          try {
            const { useAuthStore } = await import('@/store/auth')
            const auth = useAuthStore()
            userUidOrEmail = auth.user?.uid || auth.user?.email
            userEmail = auth.user?.email || ''
            if (!userUidOrEmail) {
              console.error('persistToFirestore: No userUidOrEmail found!')
              return
            }
          } catch (err) {
            console.error('persistToFirestore: Could not import/use auth:', err)
          }
        } else {
          try {
            const { useAuthStore } = await import('@/store/auth')
            const auth = useAuthStore()
            userEmail = auth.user?.email || ''
          } catch (err) {
            console.error('persistToFirestore: Could not import/use auth:', err)
          }
        }
        
        const { doc, setDoc, collection } = await import('firebase/firestore')
        const notesDoc = doc(collection(firestore, 'notes'), userUidOrEmail)
        const encodedNotes: Record<string, string> = {}
        for (const [style, note] of Object.entries(this.notes)) {
          encodedNotes[encodeFirestoreKey(style)] = note
        }
        const out: PersistShape & { userEmail?: string } = {
          v: VERSION,
          notes: encodedNotes,
          userEmail
        }
        await setDoc(notesDoc, out)
      } catch (err) {
        console.error('persistToFirestore error:', err)
        if (process.client && typeof useToast === 'function') {
          useToast().add({
            title: 'Notes sync failed',
            description: 'Could not save notes to server.',
            color: 'error',
            icon: 'i-lucide-alert-triangle'
          })
        }
      }
    },
    persistToFirestoreDebounced: (() => {
      let timeout: NodeJS.Timeout | null = null
      return function(this: any) {
        if (timeout) clearTimeout(timeout)
        timeout = setTimeout(async () => {
          const { useAuthStore } = await import('@/store/auth')
          const auth = useAuthStore()
          if (auth.user?.uid || auth.user?.email) {
            await this.persistToFirestore(auth.user?.uid || auth.user?.email)
          } else {
            console.error('persistToFirestoreDebounced: No user!')
          }
        }, 500)
      }
    })()
  }
})
